<script setup lang="ts">
import {computed} from 'vue'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import {v4 as uuid} from 'uuid'

type Emits = {
  change: [value: boolean]
}

interface Props {
  id?: string
  isDisabled?: boolean
  value: boolean
  label?: string
}

const emit = defineEmits<Emits>()

const props = withDefaults(defineProps<Props>(), {
  id: () => `checkbox-${uuid()}`,
})

const isNewUiEnabled = useNewUiEnabled()

const checkboxClasses = computed(() => [
  'pointer-events-none flex h-5 w-5 items-center justify-center rounded-new-md border-new-md transition-colors duration-150',
  'peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-new-primary-500',
  props.value
    ? props.isDisabled
      ? 'border-new-tertiary-500 bg-new-tertiary-500'
      : 'border-new-secondary-default bg-new-secondary-default peer-hover:border-new-secondary-hover peer-hover:bg-new-secondary-hover'
    : props.isDisabled
      ? 'border-new-secondary-disabled bg-white'
      : 'border-new-secondary-default bg-white peer-hover:border-new-secondary-hover',
])

function onChange(event: Event) {
  emit('change', (event.target as HTMLInputElement).checked)
}
</script>

<template>
  <label v-if="isNewUiEnabled" class="inline-flex items-center" :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'">
    <span class="relative inline-flex h-5 w-5 shrink-0 items-center justify-center">
      <input
        :id="id"
        :checked="value"
        class="peer !absolute !inset-0 h-full w-full appearance-none opacity-0"
        :class="isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
        :disabled="isDisabled"
        type="checkbox"
        @change="onChange"
      />
      <span :class="checkboxClasses">
        <svg v-if="value" aria-hidden="true" class="h-4 w-4 text-white" fill="none" viewBox="0 0 16 16">
          <path
            d="M3.5 8.25L6.5 11.25L12.5 4.75"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </span>
    </span>
    <span v-if="label" class="ml-2 text-base text-new-neutral-700">{{ label }}</span>
  </label>

  <div v-else class="flex gap-2 items-center">
    <input :id="id" :checked="value" :disabled="isDisabled" type="checkbox" @change="onChange" />
    <label v-if="label" :for="id">{{ label }}</label>
  </div>
</template>
